var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import mergeRefs from 'merge-refs';
import { forwardRef, useCallback, useMemo, useRef } from 'react';
import { SearchSVG } from '@snack-uikit/icons';
import { InputPrivate, moveCursorToEnd, useButtonNavigation, useClearButton, } from '@snack-uikit/input-private';
import { Sun } from '@snack-uikit/loaders';
import { useLocale } from '@snack-uikit/locale';
import { extractSupportProps, useValueControl } from '@snack-uikit/utils';
import { PRIVATE_SEARCH_TEST_IDS, SIZE } from '../../constants';
import styles from './styles.module.css';
export const SearchPrivate = forwardRef(function SearchPrivate(_a, ref) {
    var { size = SIZE.S, value: valueProp = '', onChange: onChangeProp, loading, placeholder, onKeyDown, onFocus, onBlur, onSubmit, className, tabIndex } = _a, rest = __rest(_a, ["size", "value", "onChange", "loading", "placeholder", "onKeyDown", "onFocus", "onBlur", "onSubmit", "className", "tabIndex"]);
    const [value = '', onValueChange] = useValueControl({
        value: valueProp,
        defaultValue: '',
        onChange: onChangeProp,
    });
    const localRef = useRef(null);
    const clearButtonRef = useRef(null);
    const { t } = useLocale('SearchPrivate');
    const showClearButton = Boolean(value);
    const onClear = () => {
        var _a;
        onValueChange('');
        (_a = localRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const clearButtonSettings = useClearButton({
        clearButtonRef,
        showClearButton,
        size,
        onClear,
    });
    const { postfixButtons, inputTabIndex, onInputKeyDown } = useButtonNavigation({
        inputRef: localRef,
        postfixButtons: useMemo(() => [clearButtonSettings], [clearButtonSettings]),
        readonly: false,
        submitKeys: ['Enter', 'Space'],
    });
    const handleKeyDown = useCallback((e) => {
        var _a;
        onKeyDown && onKeyDown(e);
        onInputKeyDown(e);
        if (e.key === 'Enter' && ((_a = localRef.current) === null || _a === void 0 ? void 0 : _a.value)) {
            onSubmit && onSubmit(localRef.current.value);
        }
    }, [onInputKeyDown, onKeyDown, onSubmit]);
    const handleOnFocus = useCallback((e) => {
        onFocus && onFocus(e);
        moveCursorToEnd(localRef.current);
    }, [onFocus]);
    return (_jsxs("div", Object.assign({ className: cn(styles.container, className) }, extractSupportProps(rest), { "data-size": size, children: [_jsx("span", { className: styles.prefix, children: loading ? (_jsx(Sun, { "data-test-id": PRIVATE_SEARCH_TEST_IDS.iconSun })) : (_jsx(SearchSVG, { "data-test-id": PRIVATE_SEARCH_TEST_IDS.iconSearch })) }), _jsx(InputPrivate, { value: value, onChange: onValueChange, onKeyDown: handleKeyDown, onFocus: handleOnFocus, onBlur: onBlur, tabIndex: tabIndex !== null && tabIndex !== void 0 ? tabIndex : inputTabIndex, ref: mergeRefs(ref, localRef), placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : t('placeholder'), type: 'text', "data-test-id": PRIVATE_SEARCH_TEST_IDS.input }), _jsx("span", { className: styles.postfix, children: postfixButtons })] })));
});
